import { useEffect, useMemo, useState } from 'react';
import { useStores } from 'Hooks/useStore';
import { isSuperAdminRole, roleDisplayLabel } from 'LearningManager/helpers/role';
import { lmsDirectoryAxiosClient } from 'LearningManager/service/lmsClient';
import { AssignmentSource } from 'Services/Api/AssignmentSources/Types';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import { DirectoryGroup } from 'Services/Api/Directory/Types';
import { useReportFilters } from '../context/ReportFilters.context';
import { fetchReportAssignmentSources } from '../reportLookups';
import { FilterKey, FilterOptionsByKey } from '../TrainingReports.types';

const toOptions = (items: { id: number | string; name: string }[]) =>
	items
		.map((item) => ({ value: String(item.id), label: item.name }))
		.sort((a, b) => a.label.localeCompare(b.label));

const fetchGroups = async (
	ids: number[],
	label: string
): Promise<DirectoryGroup[]> => {
	if (!ids.length) return [];
	const { data } = await lmsDirectoryAxiosClient.get<DirectoryGroup[]>(
		`${DIRECTORY_API.GROUPS}?id=${ids.join(
			','
		)}&include_group_tree=true&label[]=${label}`
	);
	return data;
};

const useReportFilterOptions = (
	tenantId?: number,
	scopeTenantId?: number
): FilterOptionsByKey => {
	const { learningManagerStore, assignUsersStore } = useStores();
	const { selection } = useReportFilters();
	const [assignments, setAssignments] = useState<AssignmentSource[]>([]);
	const [properties, setProperties] = useState<DirectoryGroup[]>([]);
	const [groups, setGroups] = useState<DirectoryGroup[]>([]);

	const [selectedProperty] = selection[FilterKey.Property];

	useEffect(() => {
		let active = true;
		fetchReportAssignmentSources(tenantId, true)
			.then((data) => active && setAssignments(data))
			.catch(() => active && setAssignments([]));
		learningManagerStore
			.fetchAllCourses(100, tenantId)
			.catch(() => undefined);
		return () => {
			active = false;
		};
	}, [learningManagerStore, tenantId]);

	useEffect(() => {
		if (!assignUsersStore.roles.length) {
			assignUsersStore.fetchRoles().catch(() => undefined);
		}
	}, [assignUsersStore]);

	useEffect(() => {
		let active = true;
		const request = scopeTenantId
			? fetchGroups([scopeTenantId], 'Property')
			: lmsDirectoryAxiosClient
					.get<DirectoryGroup[]>(
						`${DIRECTORY_API.GROUPS}?label[]=Property`
					)
					.then(({ data }) => data);
		request
			.then((data) => active && setProperties(data))
			.catch(() => active && setProperties([]));
		return () => {
			active = false;
		};
	}, [scopeTenantId]);

	useEffect(() => {
		let active = true;
		const propertyIds = selectedProperty
			? [Number(selectedProperty)]
			: properties.map((property) => property.id);
		fetchGroups(propertyIds, 'Group')
			.then((data) => active && setGroups(data))
			.catch(() => active && setGroups([]));
		return () => {
			active = false;
		};
	}, [properties, selectedProperty]);

	const courses = learningManagerStore.coursesById;
	const roles = assignUsersStore.roles;

	return useMemo<FilterOptionsByKey>(
		() => ({
			[FilterKey.Assignments]: toOptions(assignments),
			[FilterKey.Courses]: toOptions(Object.values(courses)),
			[FilterKey.Property]: toOptions(properties),
			[FilterKey.Group]: toOptions(groups),
			[FilterKey.Role]: roles.reduce((acc, role) => {
				if (!isSuperAdminRole(role)) {
					acc.push({
						value: String(role.id),
						label: roleDisplayLabel(role.name),
					});
				}
				return acc;
			}, [] as { value: string; label: string }[])
		}),
		[assignments, courses, properties, groups, roles]
	);
};

export default useReportFilterOptions;
